/**
 * @file Classifies a tokenised booking-link failure into the state the page renders.
 * @module utils/healthcare/bookingLinkState
 *
 * S9 of ClickUp 86d2w0fwz. `BookingForm.tsx` imports `runtimeEnv`, which reads
 * `import.meta.env` and cannot be parsed by ts-jest — so every assertion about this mapping
 * would otherwise need a component render and a `jest.mock` of the environment accessor. The
 * mapping is pure, so it lives here and is tested directly.
 *
 * ═══ WHY THREE STATES AND NOT ONE ═══════════════════════════════════════════════════════
 * The patient form link (`EnsureFormAccessToken`) answers 404 for EVERY refusal on purpose,
 * so `Form.tsx` has exactly one honest "this link no longer works" screen. A BOOKING link is
 * different: `EnsureBookingLinkToken` distinguishes consumed (410 `already_booked`) from
 * expired (410 `link_expired`) from everything else (404), because "you have already booked
 * this appointment" is reassurance the patient needs — it is the ticket's own requested
 * message — and it leaks nothing an enumerator could use. A holder of a valid-but-consumed
 * token already knows the link existed.
 *
 * ⚠️ `"ok"` here means "not a link-state failure", NOT "the request succeeded". A 500, a
 * network drop or a 422 policy refusal all classify as `"ok"` so the caller keeps its ordinary
 * inline error handling rather than replacing the whole page with a dead-end screen.
 *
 * ⚠️ The per-patient CAP refusal is a 422 carrying the same message string as
 * `already_booked`, and it is deliberately NOT mapped to that state: a cap refusal means the
 * patient has exhausted their allowance for that appointment type, not that this link was
 * used. It stays an inline error on a still-usable form.
 */

export type BookingLinkState = "ok" | "already_booked" | "expired" | "invalid";

/** The `code` key `EnsureBookingLinkToken` puts on its two 410 bodies. */
const ALREADY_BOOKED = "already_booked";
const LINK_EXPIRED = "link_expired";

/**
 * @param status HTTP status of the refusal (`error.response?.status`) — undefined on a
 *   network failure, which is not a link state.
 * @param code   The `code` key off the response body, present on the 410s only.
 */
export const classifyBookingLinkError = (status?: number, code?: string): BookingLinkState => {
	if (status === 404) return "invalid";

	if (status === 410) {
		if (code === ALREADY_BOOKED) return "already_booked";
		if (code === LINK_EXPIRED) return "expired";
		// A 410 with no code we recognise is still "gone" — the generic invalid copy is the
		// honest one. Never guess `already_booked` here: telling a patient their appointment
		// is booked when it is not is the one wrong answer that stops them rebooking.
		return "invalid";
	}

	return "ok";
};
